<script setup>
import { ref, watch, computed, onMounted, onUnmounted } from 'vue'
import { $bus, mqtt } from '@/common/events'
import { dialog } from '@/common/dialog'
import { hcu } from '@/common/hcu'
import { sleep } from '@/common/helper'
import { useWeightScale, onWeightScaleDeviceInfo, onWeightScaleStatus, onWeightScaleMeasured } from '@/composables/hardware/weightScale'
import { tare, setMode, setCalibrationValues, requestDeviceInfo } from '@/composables/hardware/weightScale'
import { canInterrupt } from '@/composables/interrupt'
import { useTranslate } from '@/composables/language'
import ScaleCalibration from './WeightScaleCalibration.vue'
import EventLog from '@/components/EventLog.vue'
import Chart from '@/components/Chart.vue'

const { t } = useTranslate();
const { weightScale } = useWeightScale();

const props = defineProps({
  device: {
    type: Object,
    default: null
  },
  user: {
    type: Object,
    default: null
  },
});

const emit = defineEmits([ "onLog" ]);
function insertLog(record) {
  emit("onLog", record);
}

const blueColor = "rgb(29 78 216)";
const yellowColor = "rgb(250 204 21)";

const sampleRate = ref([
  { id: "1", name: t('service.scale.sampleRate.1') },
  { id: "2", name: t('service.scale.sampleRate.2') },
  { id: "5", name: t('service.scale.sampleRate.5') },
  { id: "10", name: t('service.scale.sampleRate.10') },
  { id: "20", name: t('service.scale.sampleRate.20') },
  { id: "30", name: t('service.scale.sampleRate.30') },
  { id: "50", name: t('service.scale.sampleRate.50') },
]);

const averagingOptions = ref([
  { id: "none", name: t('service.scale.averagingOptions.none') },
  { id: "auto", name: t('service.scale.averagingOptions.auto') },
]);

const smoothingOptions = ref([
  { id: "none", name: t('service.scale.smoothingOptions.none') },
  { id: "5", name: t('service.scale.smoothingOptions.5') },
  { id: "10", name: t('service.scale.smoothingOptions.10') },
  { id: "20", name: t('service.scale.smoothingOptions.20') },
  { id: "50", name: t('service.scale.smoothingOptions.50') },
  { id: "70", name: t('service.scale.smoothingOptions.70') },
  { id: "90", name: t('service.scale.smoothingOptions.90') },
]);

const lastUnit = ref();
const lastValue = ref(0);
const tareValue = ref(0);
const knownWeight = ref();
const calcFactor = ref(1);

const calcFactorDescriptive = computed(() => {
  return `${calcFactor.value} enot = ${knownWeight.value} g`;
});

const calcWeight = computed(() => {
  if (lastValue.value && calcFactor.value) {
    return lastValue.value / calcFactor.value;
  }
  return null;
});

const zeroedValue = computed(() => {
  if (lastValue.value && tareValue.value) {
    return lastValue.value - tareValue.value;
  }
  return null;
});

const deviceStatus = ref(null);

const choice = ref('protocol');

function setChoice(value) {
  choice.value = value;
}

const logRecords = ref([
  // { text: "18 23", time: "13:23:00", type: "command", direction: "outbound" },
  // { text: "OK", time: "13:23:00", type: "success", direction: "inbound", elapsed: "+34ms" },
  // { text: "DONE", time: "13:23:10", type: "success", direction: "inbound", elapsed: "+10245ms" },
  // { text: "0, 4; 11", time: "13:23:11", type: "warning", direction: "inbound" },
  // { text: "200 300", time: "13:23:30", type: "command", direction: "outbound" },
  // { text: "ERR", time: "13:23:30", type: "danger", direction: "inbound" },
]);

const smoothingFactor = ref();
const avgSamplesCount = ref();
const measurementCount = ref();
const scaleFactor = ref();
const scaleOffset = ref();

const valueFormatted = computed(() => {
  if (lastUnit.value == "g") {
    return calcWeight.value?.toFixed(3);
  }
  return calcWeight.value;
});

const scaleSampleRate = computed(() => measurementCount.value);

const scaleStableSamplesCount = computed(() => {
  let sampleRate = scaleSampleRate.value;
  if (sampleRate == 1) {
    return 20; //2 seconds
  }
  else if (sampleRate < 10) {
    return 30 / sampleRate; //About 3 seconds
  }
  else if (sampleRate == 10) {
    return 3; //3 seconds
  }
  return 2; //2 measurements
});


const itemWeight = ref();
const itemsCount = computed(() => {
  if (lastUnit.value == "g" && itemWeight.value > 0) {
    return Math.round(lastValue.value / itemWeight.value).toFixed(0);
  }
  return null;
});

let x = 0;
const maxChartPoints = 50;
const chartChanges = ref([ ]);
const chartColor = ref(blueColor);

for (let i = 0; i < maxChartPoints; i++) {
  chartChanges.value.push({
    x: x++,
    y: null,
  });
}

onMounted(() => {
  canInterrupt(false, false);
  setTimeout(() => {
    requestDeviceInfo();
  }, 100);
});

onUnmounted(() => {
  canInterrupt(true, true);
});

onWeightScaleDeviceInfo((data) => {
  console.log("onDeviceInfo", data)

  logRecords.value.push({
    text: data.line,
    //time: getTimeNowFormatted(),
    //type: tareValue.value > 0 ? "warning" : "command",
    type: "warning",
    direction: "inbound"
  });

  if (logRecords.value.length > 20) {
    logRecords.value.splice(0, 1);
  }

  measurementCount.value = data.measurementCount;
  scaleFactor.value = data.scaleFactor;
  scaleOffset.value = data.scaleOffset;
});

onWeightScaleStatus((data) => {
  console.log("onStatus", data);
});

let measureSequence = [];

onWeightScaleMeasured((data) => {
  //Sequential measured values for calibration
  measureSequence.push(data.value);
  if (measureSequence.length > 10) {
    measureSequence.splice(0, 1);
  }

  //Forward to filtering and averaging
  onScaleMeasure(data);

  //data.value - measured value from sensor
  //scaleLastValue - smoothed
  //scaleStableValue - averaged
  const value = canScaleAverageValue.value ? scaleStableValue.value : scaleLastValue.value;

  let message = "" + value;
  if (data.unit == "g") {
    message = parseFloat(value).toFixed(3);
  }

  chartColor.value = (data.unit == "g" ? blueColor : yellowColor);
  addChartPoint(parseFloat(value));
  
  // if (tareValue.value > 0) {
  //   message = `${data.value - tareValue.value}`;
  // }

  logRecords.value.push({
    text: message,
    time: getTimeNowFormatted(),
    type: data.unit != "g" ? "warning" : "command",
    direction: "inbound"
  });

  if (logRecords.value.length > 20) {
    logRecords.value.splice(0, 1);
  }

  lastValue.value = value;
  lastUnit.value = data.unit;
});

function applyCalibrationValues() {
  setCalibrationValues({
    count: measurementCount.value,
    factor: scaleFactor.value,
    offset: scaleOffset.value,
  });
}

function setCalibratedMeasurementMode() {
  setMode("measure");
}

function setRawMeasurementMode() {
  setMode("raw");
}

function measureKnownWeight() {
  calcFactor.value = lastValue.value / knownWeight.value;
}

function getTimeNowFormatted() {
  return new Date().toLocaleTimeString("sl-SI");
}

function addChartPoint(value) {
  let list = chartChanges.value;
  list.push({
    x: x++,
    y: value
  });
  if (list.length > maxChartPoints) {
    list.splice(0, 1);
  }
  chartChanges.value = list;
}

function clearLogs() {
  logRecords.value = [ ];
  lastUnit.value = null;
  lastValue.value = 0;
  tareValue.value = 0;
  knownWeight.value = null;
  calcFactor.value = 1;

  //Clear chart data
  x = 0;
  let list = [ ];
  for (let i = 0; i < maxChartPoints; i++) {
    list.push({
      x: x++,
      y: null,
    });
  }
  chartChanges.value = list;
}


/*** Stabilization algorithm ***/

let scaleMeasureSequence = [ ];

const canScaleFilterValue = computed(() => smoothingFactor.value && smoothingFactor.value != "none");
const canScaleAverageValue = computed(() => avgSamplesCount.value && avgSamplesCount.value != "none");
const lastScaleFilteredValue = ref(0);
const scaleFilterCoefficient = ref(0.1); //Filter coefficient (0 < alpha < 1)  

const scaleLastValue = ref();
const scaleLastUnit = ref();
const isScaleValueStable = ref();
const scaleStableValue = ref(); //Either stable value or null

function filterWeightScaleValue(value) {
  //Reference: https://www.youtube.com/watch?v=cxJzCWz6xCA
  //const alpha = scaleFilterCoefficient.value; //Filter coefficient (0 < alpha < 1)  
  const alpha = 1 - parseInt(smoothingFactor.value) / 100;
  lastScaleFilteredValue.value = alpha * value + (1 - alpha) * lastScaleFilteredValue.value;
  return lastScaleFilteredValue.value;
}

//On weight scale measurement reply
function onScaleMeasure(data) {
  const value = canScaleFilterValue.value ? filterWeightScaleValue(data.value) : data.value;
  scaleLastValue.value = value;
  scaleLastUnit.value = data.unit;

  //Measure few consequential values to detect weight stability
  const maxSamplesCount = scaleStableSamplesCount.value;
  scaleMeasureSequence.push(value);

  //Collect enough samples...
  if (scaleMeasureSequence.length > maxSamplesCount) {
    scaleMeasureSequence.splice(0, 1);
  }

  //Then determine stability and calculate average
  if (scaleMeasureSequence.length >= maxSamplesCount) {
    let seq = scaleMeasureSequence;
    let len = seq.length;
    let stable = true;
    const tolerance = 0.05; //5% tolerance - if all consequential values are within the tolerance the value is considered stable
    const safeOffset = 30; //Artificially shift 30g above zero, because it is hard to calculate exact tolerance near zero values
    for (let i = 1; i < len; i++) {
      let prev = seq[i - 1] + safeOffset;
      let cur = seq[i] + safeOffset;
      let p = Math.abs((cur - prev) / cur);
      if (p > tolerance) {
        stable = false;
        break;
      }
    }

    //Calculate average weight from array of samples
    let avg = value;
    if (canScaleAverageValue.value) {
      let sum = 0;
      let count = Math.min(Math.ceil(maxSamplesCount / 2), len); //Better UX, more respnsive if taking half of the samples to average, not more
      for (let i = 0; i < count; i++) {
        sum += seq[len - i - 1];
      }
      avg = count > 1 ? sum / count : value; //Take last measured value if only 1 sample
    }

    //Apply stable value
    isScaleValueStable.value = stable;
    scaleStableValue.value = avg || value;
  }
}

const isCalibrationVisible = ref();
const isApplying = ref();

function calibrate() {
  isCalibrationVisible.value = true;
}

let backup = null;

async function applyEmpty() {
  console.log("[calibration] Applying empty scale...");
  isApplying.value = true;
  backup = {
    count: parseInt(measurementCount.value) || 1,
    factor: scaleFactor.value || 1,
    offset: scaleOffset.value || 0,
  };
  console.log("[calibration] Create backup of parameters", backup);
  setMode("measure");
  await sleep(100);
  setCalibrationValues({ count: 1, factor: 1, offset: 0 });
  await sleep(200);
  tare();
  await sleep(100);
  isApplying.value = false;
}

async function applyLoaded({ knownWeight }) {
  console.log("[calibration] Applying known weight...");

  isApplying.value = true;

  await sleep(2000);

  let size = measureSequence.length;
  let take = Math.min(10, size);

  let lastFewMeasurements = [];
  for (let i = size - 1; i > 0 && lastFewMeasurements.length <= take; i--) {
    lastFewMeasurements.push(measureSequence[i]);
  }

  console.log("[calibration] Last few", take, lastFewMeasurements.length, lastFewMeasurements);

  let sum = 0;
  for (let value of lastFewMeasurements) {
    sum += value;
  }

  let avg = sum / lastFewMeasurements.length;

  console.log("[calibration] Average", avg);
  console.log("[calibration] Known weight", knownWeight);

  let factor = avg / knownWeight;
  console.log("[calibration] Scale factor", factor);

  let offset = 0;
  console.log("[calibration] Scale offset", offset);

  let count = backup.count || 1;
  measurementCount.value = count.toString();

  scaleFactor.value = factor.toFixed(1);
  scaleOffset.value = offset.toString();

  console.log("[calibration] Backup before applying", backup);

  setCalibrationValues({ count, factor, offset });
  await sleep(100);

  isApplying.value = false;
}

function cancelCalibration() {
  if (backup != null) {
    setCalibrationValues(backup);
  }
  backup = null;
  isCalibrationVisible.value = false;
}

function finishCalibration() {
  backup = null;
  isCalibrationVisible.value = false;
}
</script>

<template>
  <div class="relative dark:text-white">

    <div class="">
      <!-- Top right corner -->
      <div class="flex flex-row justify-between space-x-2 mt-4">

        <div class="grid place-items-center">
          <div class="whitespace-nowrap">
            {{ $t('service.weightScale') }}
            <span class="font-bold" v-if="props.device && props.device.model">{{ props.device.model }}</span>
            <!-- <span class="font-mono text-blue-500 ml-2" v-if="props.cabinet">{{ props.cabinet.mac }}</span>
            <span class="font-mono text-purple-500 ml-2" v-if="props.cabinet">{{ props.cabinet.protocol || props.cabinet.type }} {{ props.cabinet.host }}:{{ props.cabinet.port }}</span> -->
          </div>
        </div>

        <!-- Action button -->
        <div class="flex flex-row justify-items-strech space-x-2">
          <span class="ml-1 mr-1 mt-2">
            <i class="fa fa-power-off text-yellow-300 mt-1 animate-bounce" v-if="deviceStatus == 'init' || deviceStatus == 'connecting'"></i>
            <i class="fa fa-power-off text-red-600 mt-1" v-else-if="deviceStatus == 'closed' || deviceStatus == 'error'"></i>
            <i class="fa fa-power-off text-green-400 mt-1" v-else-if="deviceStatus == 'open'"></i>
            <i class="fa fa-power-off text-slate-400 mt-1" v-else></i>
          </span>
          <Button type="secondary" @click="clearLogs()">{{ $t('service.clearLog') }}</Button>
          <Button type="primary" @click="calibrate()">{{ $t('service.calibrate') }}</Button>
        </div>

      </div>
    </div>

    <!-- Content -->
    <div class="grid sm:grid-cols-2 grid-cols-3 gap-2 w-full h-modal space-x-4 mt-2">

      <div class="h-full sm:col-span-1 col-span-2">

        <!-- Logger -->
        <div class="mt-4 pb-20 w-full h-full">
          <Label>{{ $t('admin.measured_values') }}</Label>
          <div class="w-full h-full pb-4 mt-2">
            <EventLog :records="logRecords">
              <template #empty>
                {{ $t('generic.empty') }}
              </template>
            </EventLog>
          </div>
        </div>

      </div>

      <div class="h-full">

        <!-- Input -->
        <div class="mt-2 pb-20 w-full h-full">
          <div class="grid w-full pb-4 mt-2 gap-0">


            <Label class="mt-2" v-if="lastUnit == 'g'">{{ $t('admin.measured_mass') }}</Label>
            <Label class="mt-2" v-else>{{ $t('admin.measured_value') }}</Label>
            <div class="grid grid-cols-3 gap-2">
              <TextBox v-model="valueFormatted" placeholder="--" :enabled="false" :readonly="true" class="col-span-2" />
              <Button type="warning" @click="tare()">{{ $t('admin.tare') }}</Button>
            </div>

            <!-- <Label class="mt-2">Znana masa [g]</Label>
            <TextBox v-model="knownWeight" placeholder="--" /> -->

            <div class="grid grid-cols-2 gap-2">
              <div>
                <Label class="mt-2">{{ $t('admin.item_mass') }}</Label>
                <TextBox v-model="itemWeight" placeholder="--" class="w-full" />
              </div>
              <div>
                <Label class="mt-2">{{ $t('admin.calculated_quantity') }}</Label>
                <TextBox v-model="itemsCount" placeholder="--" :readonly="true" class="w-full" />
              </div>
            </div>
            
            <div class="grid w-full pb-4 mt-8 gap-0">

              <!-- Smoothing -->
              <Label class="mt-2">{{ $t('admin.smoothing_factor') }}</Label>
              <DropDown :placeholder="$t('settings.select_item')" placeholder="--" :options="smoothingOptions" v-model="smoothingFactor" />

              <!-- Averaging -->
              <Label class="mt-2">{{ $t('admin.averaging_samples') }}</Label>
              <DropDown :placeholder="$t('settings.select_item')" placeholder="--" :options="averagingOptions" v-model="avgSamplesCount" />

              <!-- Sampling rate: how fast should the scale measure -->
              <Label class="mt-2">{{ $t('admin.sampling_rate') }}</Label>
              <DropDown :placeholder="$t('settings.select_item')" placeholder="--" :options="sampleRate" v-model="measurementCount" />

              <!-- Scale factor: a calibration coefficient -->
              <Label class="mt-2">{{ $t('admin.scale_factor') }}</Label>
              <TextBox v-model="scaleFactor" placeholder="--" />

              <!-- Scale offset: a calibration offset -->
              <Label class="mt-2">{{ $t('admin.value_offset') }}</Label>
              <TextBox v-model="scaleOffset" placeholder="--" />

              <!-- Button to apply configuration parameters -->
              <Button type="success" class="mt-2" @click="applyCalibrationValues()">{{ $t('admin.set_parameters') }}</Button>

              <!-- Toggle measurement mode (unit) -->
              <div class="grid grid-cols-2 gap-2">
                <Button type="primary" class="mt-2" @click="setCalibratedMeasurementMode()">{{ $t('admin.calibrated_values') }}</Button>
                <Button type="primary" class="mt-2" @click="setRawMeasurementMode()">{{ $t('admin.raw_values') }}</Button>
              </div>

            </div>

          </div>
        </div>

      </div>

    </div>

    <!-- Chart -->
    <div class="w-full h-[120px] fixed bottom-2 right-0">
      <Chart :color="chartColor" :data="chartChanges" />
    </div>

    <Modal size="3xl" v-if="isCalibrationVisible" @close="hideReportProblemWindow" :isHeaderVisible="false" >
      <!-- <template #header>
        <div class="flex items-center text-lg">
          {{ $t('gloves.error_label') }}
        </div>
      </template> -->
      <template #body>
        <div class="grid place-items-center">
          <ScaleCalibration :isApplying="isApplying" @applyEmpty="applyEmpty" @applyLoaded="applyLoaded" @finish="finishCalibration" @cancel="cancelCalibration" />
        </div>
      </template>
      <!-- <template #footer>
        <div class="flex justify-end">
          <Button type="primary" @click="sendReport()">Pošlji</Button>
        </div>
      </template> -->
    </Modal>

  </div>
</template>

<style scoped>
a {
  outline: none;
}

.label {
  position: relative;
  top: 5px;
  height: 30px;
  line-height: 30px;
}

.choice-button {
  @apply px-5 py-3;
}

.choice-button.choice-active {
  border-bottom: 3px solid rgb(26 86 219);
}

.button-circle {
  @apply aspect-square p-4 font-medium border border-gray-200 rounded-lg shadow-md;
  /* @apply bg-white hover:bg-gray-100 */ /* White button */
  @apply text-white bg-blue-700 hover:bg-blue-800; /* Blue button */
  position: absolute;
  /* left: 260px; */
  /* top: 260px; */
  left: 165px;
  top: 165px;
  z-index: 10;
  width: 240px;
  height: 240px;
  border-radius: 50%;
  font-size: 24px;
}

.button-circle.button-circle-static,
.button-circle.button-circle-static:hover {
  @apply bg-white border-gray-200 hover:bg-white hover:border-gray-200;
  cursor: default;
}

.button-circle.button-circle-inactive,
.button-circle.button-circle-inactive:hover {
  @apply bg-gray-100 text-gray-300 dark:bg-gray-800 dark:border-gray-700 dark:hover:bg-gray-700;
  cursor: default;
}

.button-door {
  @apply w-16;
  @apply flex justify-center aspect-square p-4 bg-white border border-gray-200 rounded-lg shadow hover:bg-gray-100 dark:bg-gray-800 dark:border-gray-700 dark:hover:bg-gray-700;
}

.button-door-label {
  @apply pt-0 text-xl;
  @apply font-bold tracking-tight dark:text-white;
}

@media only screen and (max-width: 1024px) { 
  .button-door {
    @apply w-14 p-3;
  }

  .button-door-label {
    @apply text-lg;
  }
}

.button-door.button-door-selected {
  border: 2px solid rgba(26,86,219,1);
}

.button-door.button-door-position,
.button-door.button-door-position:hover {
  @apply bg-blue-700 text-white;
  color: white !important;
  cursor: default;
}
</style>
